import { getPostHogDistinctId } from "@/lib/posthog";

const COOKIE_NAME = "vellum_attribution";
const MAX_AGE_SECONDS = 60 * 60 * 24 * 90; // 90 days
const FBC_STORAGE_KEY = "vellum_fbc";
const FBC_TTL_MS = 1000 * 60 * 60 * 24 * 90; // 90 days, mirroring Meta's _fbc cookie
const EXTERNAL_ID_KEY = "vellum_external_id"; // stable per-browser id for Meta external_id matching

export type Attribution = {
  acquisitionAngle?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
};

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const value = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${name}=`))
    ?.split("=")
    .slice(1)
    .join("=");
  return value ? decodeURIComponent(value) : undefined;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number) {
  if (typeof document === "undefined") return;
  const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secure}`;
}

function sanitize(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim().slice(0, 128);
  return trimmed.length > 0 ? trimmed : undefined;
}

export function readAttribution(): Attribution {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as Attribution;
    return {
      acquisitionAngle: sanitize(parsed.acquisitionAngle),
      utmSource: sanitize(parsed.utmSource),
      utmMedium: sanitize(parsed.utmMedium),
      utmCampaign: sanitize(parsed.utmCampaign),
    };
  } catch {
    return {};
  }
}

/**
 * First-touch capture per field: keep existing attribution, but fill any
 * missing fields if a later navigation carries them.
 */
export function captureAttributionFromUrl(search: string): void {
  if (typeof document === "undefined") return;

  const params = new URLSearchParams(search);
  const fromUrl: Attribution = {
    acquisitionAngle: sanitize(params.get("angle")),
    utmSource: sanitize(params.get("utm_source")),
    utmMedium: sanitize(params.get("utm_medium")),
    utmCampaign: sanitize(params.get("utm_campaign")),
  };

  const hasAny = fromUrl.acquisitionAngle || fromUrl.utmSource || fromUrl.utmMedium || fromUrl.utmCampaign;
  if (!hasAny) return;

  const existing = readAttribution();
  const attr: Attribution = {
    acquisitionAngle: existing.acquisitionAngle ?? fromUrl.acquisitionAngle,
    utmSource: existing.utmSource ?? fromUrl.utmSource,
    utmMedium: existing.utmMedium ?? fromUrl.utmMedium,
    utmCampaign: existing.utmCampaign ?? fromUrl.utmCampaign,
  };

  if (
    attr.acquisitionAngle === existing.acquisitionAngle &&
    attr.utmSource === existing.utmSource &&
    attr.utmMedium === existing.utmMedium &&
    attr.utmCampaign === existing.utmCampaign
  ) return;

  writeCookie(COOKIE_NAME, JSON.stringify(attr), MAX_AGE_SECONDS);
}

/**
 * The angle a landing page stands for, stored first-touch when the visitor has none yet: an SEO page
 * renders `data-landing-angle="seo-<slug>"`, so an upload started later on a URL without ?angle=
 * still attributes to the page the visitor came in on. Runs after captureAttributionFromUrl, so an
 * ?angle= on the landing URL wins, and it never overwrites a stored angle.
 */
export function captureLandingAngle(angle: string | undefined): void {
  const value = sanitize(angle);
  if (!value || typeof document === "undefined") return;
  const existing = readAttribution();
  if (existing.acquisitionAngle) return;
  writeCookie(COOKIE_NAME, JSON.stringify({ ...existing, acquisitionAngle: value }), MAX_AGE_SECONDS);
}

type StoredFbc = { fbc: string; expiresAt: number };

/**
 * First-touch capture of the Meta click ID. If the URL has `fbclid` and we
 * haven't stored a value yet, build the `_fbc`-format string with the current
 * timestamp and persist it. The timestamp must remain stable across events
 * for the same click, so we never overwrite an existing entry.
 */
export function captureFbcFromUrl(search: string): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(search);
  // The fbclid must reach Meta byte-for-byte. Do NOT run it through sanitize():
  // its .slice(0, 128) truncates the click ID (modern fbclids exceed 128 chars),
  // and any trim/case change makes the fbc unmatchable and hurts attribution.
  const rawFbclid = params.get("fbclid");
  const fbclid = rawFbclid && rawFbclid.length > 0 ? rawFbclid : undefined;
  if (!fbclid) return;

  try {
    const existing = window.localStorage.getItem(FBC_STORAGE_KEY);
    if (existing) {
      const parsed = JSON.parse(existing) as StoredFbc;
      if (parsed?.fbc && parsed.expiresAt > Date.now()) return;
    }
    const now = Date.now();
    const payload: StoredFbc = {
      fbc: `fb.1.${now}.${fbclid}`,
      expiresAt: now + FBC_TTL_MS,
    };
    window.localStorage.setItem(FBC_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage may be unavailable (private mode, quota); silently skip.
  }
}

export function readStoredFbc(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(FBC_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as StoredFbc;
    if (!parsed?.fbc || parsed.expiresAt <= Date.now()) {
      window.localStorage.removeItem(FBC_STORAGE_KEY);
      return undefined;
    }
    return parsed.fbc;
  } catch {
    return undefined;
  }
}

/**
 * Stable, persistent per-browser identifier sent to Meta as `external_id` on
 * both the Pixel (advanced matching) and the server-side Conversions API. It
 * does not depend on an ad click, so it boosts match quality and event-ID
 * deduplication coverage even for organic/non-`fbc` traffic.
 *
 * We seed it from the PostHog (anonymous) distinct id so Meta's `external_id`
 * maps 1:1 to our PostHog person, letting us join ad conversions to product
 * analytics. The seed is then FROZEN in a cookie + localStorage (90 days): we
 * always prefer the stored value on later reads. Freezing keeps external_id
 * identical across the Pixel, the Conversions API, and the whole journey
 * (preview → checkout → purchase), whatever posthog-js does with its own cookie.
 * A returning PostHog identity is preserved independently if legacy stores
 * disagree; changing this frozen Meta ID would break existing attribution.
 */
export function getOrCreateExternalId(): string | undefined {
  if (typeof window === "undefined") return undefined;

  // Prefer an already-frozen value so external_id stays stable across the whole
  // journey.
  let id = readCookie(EXTERNAL_ID_KEY);
  if (!id) {
    try {
      id = window.localStorage.getItem(EXTERNAL_ID_KEY) ?? undefined;
    } catch {
      // localStorage may be unavailable; fall through.
    }
  }

  // First touch: harvest the PostHog distinct id when PostHog already runs.
  // PostHog init is deferred (DeferredThirdParty), so on a genuinely first
  // touch this is usually undefined and we mint the seed ourselves below —
  // initPostHog() seeds a new SDK identity via get_device_id,
  // keeping external_id and the PostHog anonymous id identical either way.
  if (!id) {
    id = getPostHogDistinctId();
  }

  // Last resort: mint one so an event never goes out without an external_id.
  if (!id) {
    id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `ext-${Math.random().toString(16).slice(2)}${Math.random().toString(16).slice(2)}`;
  }

  // Persist to both stores: freezes the seed, refreshes TTL, heals a lost store.
  writeCookie(EXTERNAL_ID_KEY, id, MAX_AGE_SECONDS);
  try {
    window.localStorage.setItem(EXTERNAL_ID_KEY, id);
  } catch {
    // ignore
  }
  return id;
}
